import { Component, ElementRef, HostListener, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FileMedia } from '../../models/file-media.model';
import { NoticeViolation } from '../../models/notice-violation.model';
import { ImageService } from '../../services/image.service';
import { NeighborhoodService } from '../../services/neighborhood.service';
import { NoticeViolationService } from '../../services/notice-violation.service';
import { SpinnerService } from '../../services/spinner.service';

@Component({
  selector: 'app-notice-violation-modal',
  templateUrl: './notice-violation-modal.component.html',
  styleUrl: './notice-violation-modal.component.scss'
})
export class NoticeViolationModalComponent implements OnInit {

  @Input() noticeViolation!: NoticeViolation;
  @Input() neighborhoodId: string = '';

  /** false = solo lectura (ver), true = edita título, descripción, estatus y archivos. */
  @Input() editable: boolean = false;

  public readonly titleMaxLength: number = 45;

  public formNoticeViolation!: FormGroup;
  public submitted: boolean = false;

  /* Las miniaturas son botones reales para poder devolverles el foco cuando se
     cierra el visor (mismo patrón que report-modal). */
  @ViewChildren('photoThumb') photoThumbs?: QueryList<ElementRef<HTMLButtonElement>>;

  /* Estado del visor a pantalla completa (p-galleria). */
  public viewerVisible: boolean = false;
  public viewerIndex: number = 0;

  /* Miniatura desde la que se abrió el visor, para devolverle el foco al
     cerrar. No es lo mismo que viewerIndex, que va cambiando al navegar. */
  private viewerOpenerIndex: number = 0;

  /* Caché de las fotos: noticeViolation.images arma un arreglo nuevo en cada
     lectura y p-galleria lo vería como un [value] distinto en cada ciclo de
     detección de cambios. */
  private imagesSource: FileMedia[] | null = null;
  private imagesLength: number = -1;
  private imagesCache: FileMedia[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private imageService: ImageService,
    public neighborhoodService: NeighborhoodService,
    public noticeViolationService: NoticeViolationService,
    private spinnerService: SpinnerService
  ) { }

  get formControls() {
    return this.formNoticeViolation.controls;
  }

  get titleLength(): number {
    return (this.formControls['title'].value ?? '').length;
  }

  /* -------------------------------------------------------------------------- */
  /*                                 ANCHOR fotos                               */
  /* -------------------------------------------------------------------------- */
  // region fotos
  /* Solo las imágenes: los PDF se abren aparte y no entran al visor. */
  get images(): FileMedia[] {

    const files = this.noticeViolation.files;

    if (
      this.imagesSource !== files ||
      this.imagesLength !== files.length
    ) {
      this.imagesSource = files;
      this.imagesLength = files.length;
      this.imagesCache = this.noticeViolation.images;
    }

    return this.imagesCache;

  }

  /* Con una sola foto no hay a dónde navegar: sin flechas, sin contador y sin
     circular. */
  get hasManyImages(): boolean {
    return this.images.length > 1;
  }

  /* Los puntos indicadores solo ayudan mientras se puedan contar de un vistazo;
     pasando de 10 basta el contador "3 de 12". */
  get showViewerIndicators(): boolean {
    return this.images.length > 1 && this.images.length <= 10;
  }

  /* Texto alternativo real para lector de pantalla. */
  photoAlt(index: number): string {
    return `Foto ${index + 1} de ${this.images.length} de la amonestación`;
  }

  /* El template "item" del galleria recibe el archivo, no su índice. */
  photoAltOf(file: FileMedia): string {
    return this.photoAlt(this.images.indexOf(file));
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR openViewer                             */
  /* -------------------------------------------------------------------------- */
  // region openViewer
  openViewer(
    index: number
  ) {
    /* El índice se asigna ANTES que visible y en el mismo tick: p-galleria crea
       su contenido con *ngIf="visible" y lee activeIndex al construirse. */
    this.viewerIndex = index;
    this.viewerOpenerIndex = index;
    this.viewerVisible = true;
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR closeViewer                             */
  /* -------------------------------------------------------------------------- */
  // region closeViewer
  closeViewer() {

    if (!this.viewerVisible) {
      return;
    }

    this.viewerVisible = false;
    this.restoreThumbFocus();

  }

  /* p-galleria también se cierra solo (botón X o click en la máscara) y avisa
     por visibleChange. */
  onViewerVisibleChange(
    visible: boolean
  ) {

    this.viewerVisible = visible;

    if (!visible) {
      this.restoreThumbFocus();
    }

  }

  private restoreThumbFocus() {

    const thumb = this.photoThumbs?.get(this.viewerOpenerIndex);

    if (!thumb) {
      return;
    }

    /* El pFocusTrap del galleria sigue vivo durante la animación de cierre y
       reclamaría el foco: se devuelve después. */
    setTimeout(
      () => thumb.nativeElement.focus(),
      200
    );

  }

  /* -------------------------------------------------------------------------- */
  /*                          ANCHOR onDocumentKeydown                          */
  /* -------------------------------------------------------------------------- */
  // region onDocumentKeydown
  /* PrimeNG solo atiende flechas con el foco sobre las miniaturas o los
     indicadores del galleria y no cierra con Esc en pantalla completa: el
     teclado del visor se resuelve aquí. */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(
    event: KeyboardEvent
  ) {

    if (!this.viewerVisible) {
      return;
    }

    switch (event.key) {

      case 'Escape':
        /* preventDefault evita que el modal de ng-bootstrap se cierre junto con
           el visor. */
        event.preventDefault();
        this.closeViewer();
        break;

      case 'ArrowRight':
        if (!this.hasManyImages) {
          return;
        }
        event.preventDefault();
        this.viewerIndex = (this.viewerIndex + 1) % this.images.length;
        break;

      case 'ArrowLeft':
        if (!this.hasManyImages) {
          return;
        }
        event.preventDefault();
        this.viewerIndex = (this.viewerIndex - 1 + this.images.length) % this.images.length;
        break;

    }

  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR ngOnInit                              */
  /* -------------------------------------------------------------------------- */
  // region ngOnInit
  ngOnInit() {

    this.formNoticeViolation = this.formBuilder.group({
      title: [
        this.noticeViolation.title,
        [Validators.required, Validators.maxLength(this.titleMaxLength)]
      ],
      description: [this.noticeViolation.description, Validators.required],
      status: [this.noticeViolation.status]
    });

  }

  /* -------------------------------------------------------------------------- */
  /*                                 ANCHOR save                                */
  /* -------------------------------------------------------------------------- */
  // region save
  async save() {

    this.submitted = true;

    if (this.formNoticeViolation.invalid) {
      return;
    }

    this.noticeViolation.title = this.formControls['title'].value.trim();
    this.noticeViolation.description = this.formControls['description'].value.trim();
    this.noticeViolation.status = this.formControls['status'].value;

    const flag = await this.noticeViolationService.update(
      this.noticeViolation
    );

    if (flag) {
      this.activeModal.close(this.noticeViolation);
    }

  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR refresh                               */
  /* -------------------------------------------------------------------------- */
  /* Tras agregar o quitar archivos se vuelve a pedir la amonestación para
     pintar la lista real y se refresca la tabla de fondo. Los cambios sin
     guardar del formulario no se tocan. */
  private async refresh() {

    const noticeViolation = await this.noticeViolationService.get(
      this.noticeViolation.neighborhoodId,
      this.noticeViolation.noticeViolationId
    );

    if (noticeViolation) {
      this.noticeViolation.files = noticeViolation.files;
    }

    this.noticeViolationService.getAll(
      this.noticeViolation.neighborhoodId
    );

  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR compressFile                            */
  /* -------------------------------------------------------------------------- */
  // region compressFile
  async compressFile() {

    const images = await this.imageService.compressFile();

    if (images.length === 0) {
      return;
    }

    await this.uploadFiles(images);

  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR onPdfChange                             */
  /* -------------------------------------------------------------------------- */
  // region onPdfChange
  async onPdfChange(
    event: Event
  ) {

    const input = event.target as HTMLInputElement;

    const files = Array.from(input.files ?? []).filter(
      (file) => this.noticeViolationService.isValidPdf(file)
    );

    /* Se limpia el input para poder volver a elegir el mismo archivo. */
    input.value = '';

    if (files.length === 0) {
      return;
    }

    await this.uploadFiles(files);

  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR uploadFiles                             */
  /* -------------------------------------------------------------------------- */
  private async uploadFiles(
    files: Array<File | string>
  ) {

    for (let i = 0; i < files.length; i++) {

      const current = files[i];

      this.spinnerService.loading = true;

      await this.noticeViolationService.uploadFile(
        this.noticeViolation,
        current,
        typeof current === 'string' ? `Foto ${i + 1}` : current.name,
        {
          index: i + 1,
          total: files.length
        }
      );

    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    await this.refresh();

  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR deleteFile                              */
  /* -------------------------------------------------------------------------- */
  // region deleteFile
  async deleteFile(
    file: FileMedia
  ) {

    const flag = confirm(
      `¿Está seguro que desea quitar este ${file.fileType === 'pdf' ? 'PDF' : 'archivo'} de la amonestación?`
    );

    if (!flag) {
      return;
    }

    const deleted = await this.noticeViolationService.deleteFile(
      this.noticeViolation,
      file.fileId
    );

    if (deleted) {
      await this.refresh();
    }

  }

}
